import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Rating,
  Button,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  LocalFireDepartment as FireIcon,
  Diamond as DiamondIcon,
} from '@mui/icons-material';

export interface Freelancer {
  id: string;
  name: string;
  title: string;
  avatar: string;
  rating: number;
  reviewCount: number;
  location: string;
  skills: string[];
  hourlyRate: number;
  completedProjects: number;
  responseTime: string;
  description: string;
  isOnline: boolean;
  isVerified: boolean;
  // Extended profile fields
  email?: string;
  phone?: string;
  website?: string;
  linkedin?: string;
  github?: string;
  experience: string;
  education: Education[];
  certifications: Certification[];
  workHistory: WorkHistory[];
  portfolio: PortfolioItem[];
  languages: Language[];
  availability: string;
  timezone: string;
  joinedDate: string;
}

export interface Education {
  id: string;
  institution: string;
  degree: string;
  field: string;
  startDate: string;
  endDate: string;
  description?: string;
}

export interface Certification {
  id: string;
  name: string;
  issuer: string;
  issueDate: string;
  expiryDate?: string;
  credentialId?: string;
  credentialUrl?: string;
}

export interface WorkHistory {
  id: string;
  company: string;
  position: string;
  startDate: string;
  endDate?: string;
  description: string;
  current: boolean;
}

export interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  projectUrl?: string;
  technologies: string[];
  completedDate: string;
}

export interface Language {
  name: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Native';
}

interface FreelancerCardProps {
  freelancer: Freelancer;
  onViewProfile: (freelancer: Freelancer) => void;
  isPromoted?: boolean;
  onTogglePromotion?: (freelancerId: string) => void;
  canPromote?: boolean;
}

const FreelancerCard: React.FC<FreelancerCardProps> = ({
  freelancer,
  onViewProfile,
  isPromoted = false,
  onTogglePromotion,
  canPromote = false
}) => {
  const { t } = useTranslation();

  return (
    <Card
      sx={{
        background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
        border: '1px solid rgba(40, 160, 240, 0.2)',
        borderRadius: { xs: 2, sm: 4 },
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: { xs: 'none', sm: 'translateY(-8px) scale(1.02)' },
          boxShadow: { 
            xs: '0 4px 12px rgba(40, 160, 240, 0.2)', 
            sm: '0 20px 40px rgba(40, 160, 240, 0.3), 0 0 0 1px rgba(40, 160, 240, 0.4)' 
          },
          '&::before': {
            opacity: 1,
          },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          opacity: 0,
          transition: 'opacity 0.4s ease',
          zIndex: 0,
        },
      }}
    >
      {/* Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 80,
          height: 80,
          background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          borderRadius: '50%',
          zIndex: 1,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(40, 160, 240, 0.1) 100%)',
          borderRadius: '50%',
          zIndex: 1,
        }}
      />

      <CardContent sx={{ p: { xs: 2, sm: 3 }, position: 'relative', zIndex: 2 }}>
        {/* Promotion Badge */}
        {isPromoted && (
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 3 }}>
          <Chip
            label={t('common.promoted')}
            icon={<FireIcon sx={{ fontSize: '0.9rem' }} />}
            sx={{
              background: 'linear-gradient(135deg, #ff5722 0%, #d32f2f 100%)',
              color: '#ffffff',
              border: '2px solid #ff5722',
              fontSize: '0.75rem',
              fontWeight: 'bold',
              height: 32,
              px: 1.5,
              boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)',
              animation: 'pulse 2s infinite, glow 3s infinite',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.05)' },
                '100%': { transform: 'scale(1)' },
              },
              '@keyframes glow': {
                '0%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
                '50%': { boxShadow: '0 6px 20px rgba(255, 87, 34, 0.8)' },
                '100%': { boxShadow: '0 4px 12px rgba(255, 87, 34, 0.4)' },
              },
            }}
          />
          </Box>
        )}

        {/* Header Section */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          justifyContent: 'space-between', 
          mb: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={freelancer.avatar}
                sx={{
                  width: { xs: 60, sm: 70 },
                  height: { xs: 60, sm: 70 },
                  border: '3px solid rgba(40, 160, 240, 0.4)',
                  boxShadow: '0 0 20px rgba(40, 160, 240, 0.3)',
                }}
              />
              {freelancer.isOnline && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    right: 2,
                    width: { xs: 16, sm: 18 },
                    height: { xs: 16, sm: 18 },
                    borderRadius: '50%',
                    backgroundColor: '#10b981',
                    border: '3px solid #1a1a2e',
                    boxShadow: '0 0 10px rgba(16, 185, 129, 0.5)',
                  }}
                />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    color: '#ffffff',
                    fontSize: { xs: '1.1rem', sm: '1.2rem' },
                    background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    wordBreak: 'break-word',
                  }}
                >
                  {freelancer.name}
                </Typography>
                {freelancer.isVerified && (
                  <CheckCircleIcon 
                    sx={{ 
                      color: '#10b981', 
                      fontSize: { xs: '1.1rem', sm: '1.2rem' },
                      filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                    }} 
                  />
                )}
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: '#28A0F0',
                  fontWeight: 600,
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  mb: 0.5,
                  wordBreak: 'break-word',
                }}
              >
                {freelancer.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Rating
                  value={freelancer.rating}
                  precision={0.1}
                  readOnly
                  size="small"
                  sx={{
                    '& .MuiRating-iconFilled': { color: '#fbbf24' },
                    '& .MuiRating-iconEmpty': { color: '#374151' },
                  }}
                />
                <Typography variant="body2" sx={{ 
                  color: '#a0a0a0', 
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  whiteSpace: 'nowrap'
                }}>
                  {freelancer.rating} ({freelancer.reviewCount} отзива)
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ 
            textAlign: { xs: 'left', sm: 'right' },
            alignSelf: { xs: 'flex-start', sm: 'flex-end' },
            mt: { xs: 1, sm: 0 }
          }}>
            <Typography
              variant="h4"
              sx={{
                color: '#28A0F0',
                fontWeight: 800,
                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5,
              }}
            >
              ${freelancer.hourlyRate}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: '#a0a0a0', 
              fontSize: { xs: '0.75rem', sm: '0.8rem' } 
            }}>
              на час
            </Typography>
          </Box>
        </Box>

        {/* Info Section */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 2, sm: 3 }, 
          mb: 2,
          flexWrap: 'wrap',
          rowGap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LocationIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#28A0F0' }} />
            <Typography variant="body2" sx={{ 
              color: '#ffffff', 
              fontWeight: 500,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}>
              {freelancer.location}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WorkIcon sx={{ fontSize: { xs: 14, sm: 16 }, color: '#10b981' }} />
            <Typography variant="body2" sx={{ 
              color: '#ffffff', 
              fontWeight: 500,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}>
              {freelancer.completedProjects} проекта
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" sx={{ 
              color: '#a0a0a0',
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}>
              Отговор: {freelancer.responseTime}
            </Typography>
          </Box>
        </Box>

        {/* Skills Section */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ 
            color: '#a0a0a0', 
            mb: 1, 
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.875rem' }
          }}>
            Ключови умения:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 } }}>
            {freelancer.skills.slice(0, 5).map((skill, index) => (
              <Chip
                key={index}
                label={skill}
                sx={{
                  background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
                  color: '#ffffff',
                  border: '1px solid rgba(40, 160, 240, 0.4)',
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  height: { xs: 24, sm: 28 },
                  display: { xs: index < 3 ? 'flex' : 'none', sm: 'flex' },
                  '&:hover': {
                    background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.3) 0%, rgba(236, 72, 153, 0.3) 100%)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
            {freelancer.skills.length > 5 && (
              <Chip
                label={`+${freelancer.skills.length - 5} още`}
                sx={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: '#a0a0a0',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.8rem' },
                  height: { xs: 24, sm: 28 },
                }}
              />
            )}
          </Box>
        </Box>

        {/* Description */}
        <Typography
          variant="body2"
          sx={{
            color: '#d0d0d0',
            lineHeight: 1.6,
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
            display: '-webkit-box',
            WebkitLineClamp: { xs: 3, sm: 2 },
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {freelancer.description}
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            endIcon={<VisibilityIcon />}
            onClick={() => onViewProfile(freelancer)}
            sx={{
              background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
              color: '#ffffff',
              px: { xs: 3, sm: 4 },
              py: { xs: 1.2, sm: 1.5 },
              fontWeight: 700,
              borderRadius: { xs: 2, sm: 3 },
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              minHeight: { xs: 40, sm: 44 },
              flex: 1,
              boxShadow: '0 8px 32px rgba(40, 160, 240, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ec4899 0%, #28A0F0 100%)',
                transform: { xs: 'none', sm: 'translateY(-2px)' },
                boxShadow: { 
                  xs: '0 8px 32px rgba(40, 160, 240, 0.3)', 
                  sm: '0 12px 40px rgba(40, 160, 240, 0.4)' 
                },
              },
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Преглед на профила
          </Button>
        {onTogglePromotion && canPromote && (
          <Button
            variant="outlined"
            onClick={() => onTogglePromotion(freelancer.id)}
            startIcon={isPromoted ? <FireIcon /> : <DiamondIcon />}
            sx={{
              color: isPromoted ? '#ff5722' : '#a0a0a0',
              borderColor: isPromoted ? '#ff5722' : 'rgba(255, 255, 255, 0.3)',
              minWidth: 'auto',
              px: 1.5,
              py: 1,
              '&:hover': {
                backgroundColor: isPromoted ? 'rgba(255, 87, 34, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                borderColor: isPromoted ? '#ff5722' : '#ffffff',
              }
            }}
          >
            {isPromoted ? 'Управлявай' : 'Промотирай'}
          </Button>
        )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default FreelancerCard;
